import LegalLayout, { LegalSection } from "@/pages/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      label="Privacidad"
      title="Tu familia es tuya. Sus datos también."
      updated="Junio de 2026"
      intro="En Kiddio existimos para darte tranquilidad, no para vigilar a nadie. Esta política explica, en lenguaje claro, qué datos tratamos y por qué."
    >
      <LegalSection n="01" title="Quiénes somos">
        <p>
          Kiddio es una marca de relojes GPS pensados para que los niños exploren con libertad y sus familias estén tranquilas.
          El responsable del tratamiento de los datos es Kiddio. Puedes contactarnos en cualquier momento en{" "}
          <strong className="text-[#12263A]">privacidad@kiddio.com</strong>.
        </p>
      </LegalSection>

      <LegalSection n="02" title="Nuestro compromiso con las familias">
        <p>Antes de entrar en detalle, tres promesas que guían todo lo que hacemos:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-[#12263A]">Nunca vendemos</strong> tus datos ni los de tu hijo a terceros.</li>
          <li><strong className="text-[#12263A]">Sin publicidad ni redes sociales</strong> dentro del reloj ni de la app.</li>
          <li>Recogemos <strong className="text-[#12263A]">lo mínimo imprescindible</strong> para que el servicio funcione.</li>
        </ul>
      </LegalSection>

      <LegalSection n="03" title="Qué datos tratamos">
        <p>Distinguimos entre los datos del adulto que gestiona la cuenta y los del niño que lleva el reloj:</p>
        <p><strong className="text-[#12263A]">Del padre, madre o tutor:</strong> nombre y correo electrónico (a través de tu inicio de sesión con Google), y los datos necesarios para el pago, que gestiona de forma segura nuestro proveedor Stripe. Kiddio no almacena los números completos de tu tarjeta.</p>
        <p><strong className="text-[#12263A]">Del niño y del reloj:</strong> la ubicación aproximada del dispositivo, las zonas seguras que tú creas, los contactos que apruebas, y los avisos del botón SOS. Estos datos solo son visibles para las personas que tú autorizas.</p>
      </LegalSection>

      <LegalSection n="04" title="Datos de menores">
        <p>
          Tratamos los datos de los niños con un cuidado especial. La cuenta siempre pertenece a un adulto responsable, que decide
          qué se comparte y con quién. En cualquier momento puedes revisar, corregir o eliminar la información asociada a tu hijo
          desde tu cuenta o escribiéndonos.
        </p>
      </LegalSection>

      <LegalSection n="05" title="Para qué usamos los datos">
        <ul className="list-disc pl-5 space-y-2">
          <li>Mostrarte dónde está tu hijo cuando lo necesitas.</li>
          <li>Avisarte, de forma amable, cuando entra o sale de una zona segura.</li>
          <li>Enviar la alerta del botón SOS a los contactos de la familia.</li>
          <li>Gestionar tu compra, tu plan de datos y enviarte confirmaciones y avisos importantes.</li>
          <li>Ofrecerte soporte y mantener el servicio seguro.</li>
        </ul>
        <p>No usamos la ubicación de tu hijo para fines comerciales ni para elaborar perfiles publicitarios.</p>
      </LegalSection>

      <LegalSection n="06" title="Con quién compartimos datos">
        <p>Solo trabajamos con proveedores de confianza y únicamente para poder prestarte el servicio:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-[#12263A]">Google</strong>, para que puedas iniciar sesión de forma segura.</li>
          <li><strong className="text-[#12263A]">Stripe</strong>, para procesar los pagos.</li>
          <li><strong className="text-[#12263A]">Resend</strong>, para enviarte correos de confirmación y avisos.</li>
          <li><strong className="text-[#12263A]">Twilio</strong>, para enviarte los códigos de verificación por SMS.</li>
        </ul>
        <p>
          Estos proveedores actúan como <strong className="text-[#12263A]">encargados del tratamiento</strong> (subcontratistas de
          soporte) siguiendo únicamente nuestras instrucciones para prestar el servicio; no son terceros que usen tus datos para su
          propio marketing. Nunca compartimos datos con anunciantes ni redes sociales.
        </p>
      </LegalSection>

      <LegalSection n="07" title="Mensajes SMS y consentimiento">
        <p>
          Cuando eliges recibir mensajes SMS de Kiddio, recopilamos y utilizamos tu número de teléfono y tu consentimiento
          para enviarte los mensajes necesarios para la verificación de tu cuenta y el inicio de sesión.
        </p>
        <p>
          Los números de teléfono móvil, la información de consentimiento para SMS y los datos de opt-in no se venden,
          alquilan ni comparten con terceros o afiliados para sus propios fines de marketing o promocionales.
        </p>
        <p>
          Kiddio utiliza los servicios de proveedores tecnológicos necesarios para prestar el servicio y enviar mensajes de
          verificación. Estos proveedores actúan únicamente para proporcionar los servicios solicitados por Kiddio y no pueden
          utilizar tu información de consentimiento SMS para sus propios fines de marketing o promocionales.
        </p>
        <p>
          Los mensajes SMS de Kiddio se utilizan para códigos de verificación de un solo uso y acceso seguro a la cuenta. La
          frecuencia de los mensajes depende de la actividad de tu cuenta. Pueden aplicarse tarifas de mensajes y datos según
          tu operador móvil.
        </p>
        <p>
          Puedes dejar de recibir mensajes SMS respondiendo <strong className="text-[#12263A]">STOP</strong> a cualquier mensaje
          SMS de Kiddio.
        </p>
        <p className="font-bold text-[#12263A]">
          Ninguna información móvil (número de teléfono) ni los datos de consentimiento y opt-in de SMS se compartirán con terceros
          ni afiliados con fines de marketing o promocionales. Se permite el intercambio de información con subcontratistas de
          servicios de soporte, como atención al cliente y el proveedor de mensajería. Todas las demás categorías de uso excluyen
          los datos de opt-in y consentimiento de mensajes de texto; esta información no se compartirá con ningún tercero.
        </p>
        <p className="text-[13px] text-[#6E7B8B] leading-[1.6]">
          (English) No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.
          Information sharing to subcontractors in support services, such as customer service and the messaging provider, is
          permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will
          not be shared with any third parties.
        </p>
      </LegalSection>

      <LegalSection n="08" title="Ubicación y localización">
        <p>
          La localización existe para darte tranquilidad, no para controlar. Tú decides las zonas seguras y quién puede ver la
          ubicación. Hablamos de libertad para tu hijo, no de vigilancia.
        </p>
      </LegalSection>

      <LegalSection n="09" title="Cuánto tiempo conservamos los datos">
        <p>
          Mantenemos los datos mientras tengas una cuenta activa. La información de ubicación se conserva solo el tiempo
          necesario para el servicio y luego se elimina. Si cierras tu cuenta, borramos tus datos salvo lo que la ley nos obligue
          a conservar (por ejemplo, facturas).
        </p>
      </LegalSection>

      <LegalSection n="10" title="Seguridad">
        <p>
          Ciframos las comunicaciones y protegemos tu información con medidas técnicas y organizativas. El acceso a los datos está
          restringido a lo estrictamente necesario.
        </p>
      </LegalSection>

      <LegalSection n="11" title="Tus derechos">
        <p>
          Puedes acceder, rectificar, eliminar o exportar tus datos, así como oponerte a determinados tratamientos. Escríbenos a{" "}
          <strong className="text-[#12263A]">privacidad@kiddio.com</strong> y te ayudamos.
        </p>
      </LegalSection>

      <LegalSection id="cookies" n="12" title="Cookies">
        <p>
          Usamos las cookies mínimas necesarias para mantener tu sesión iniciada y para que el sitio funcione. No usamos cookies de
          publicidad ni de seguimiento comercial.
        </p>
      </LegalSection>

      <LegalSection n="13" title="Cambios en esta política">
        <p>
          Si actualizamos esta política, te avisaremos y publicaremos la nueva fecha arriba. Los cambios importantes te los
          comunicaremos por correo.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
